import { useLeadStore } from '@/store/LeadStore';
import { useGlobalVuelidate } from '@/store/LeadValidator';

import { isFunction, isInteger } from 'lodash';
import { StripQuery } from '@/helpers/StripQuery';
import { MunicipalitySubsidyService } from '@/services/MunicipalitySubsidyService';
import { isEnergyLabelAtMost, normalizeEnergyLabel } from '@/helpers/EnergyLabel';

const splitEnergyMunicipalities = ['Pekela', 'Midden-Groningen', 'Veendam', 'Stadskanaal', 'Oldambt', 'Westerwolde'];

function getVeendamEnergyLabelCategory(lead) {
  const first = lead.otherSubsidies?.energyLabelDOrLower;
  const second = lead.otherSubsidies?.hasTwoPoorInsulatedParts;
  const vhfB = lead.otherSubsidies?.vhfEnergyLabelBOrLower;

  if (first === true) {
    return 'dToG';
  }

  if (first === 'unknown' || vhfB === 'unknown' || second === 'unknown') {
    return 'unknown';
  }

  if (first === false && vhfB === true) {
    return 'bOrC';
  }

  if (first === false && vhfB === false) {
    return 'aOrBetter';
  }

  return null;
}

function hasAnyEnergyLabelQuestion(lead) {
  const municipality = lead.address?.pc_GemNaam || lead.address?.Municipality;
  const usesSplitEnergyQuestions = splitEnergyMunicipalities.includes(municipality);

  if (!usesSplitEnergyQuestions) {
    return false;
  }

  const usesVeendamSingleEnergyLabelQuestion = municipality === 'Veendam';
  const knownEnergyLabel = normalizeEnergyLabel(lead.address?.Energielabel__c);
  const hasKnownEnergyLabel = Boolean(knownEnergyLabel);
  const knownAtMostD = hasKnownEnergyLabel
    ? isEnergyLabelAtMost(knownEnergyLabel, 'D', { missingAsPass: false })
    : null;

  const veendamEnergyLabelCategory = usesVeendamSingleEnergyLabelQuestion
    ? getVeendamEnergyLabelCategory(lead)
    : null;

  const isSplitSectionComplete = (() => {
    if (hasKnownEnergyLabel) {
      if (knownAtMostD === true) {
        return true;
      }

      return lead.otherSubsidies?.hasTwoPoorInsulatedParts !== null;
    }

    if (usesVeendamSingleEnergyLabelQuestion) {
      if (veendamEnergyLabelCategory === null) {
        return false;
      }

      if (veendamEnergyLabelCategory === 'dToG') {
        return true;
      }

      return lead.otherSubsidies?.hasTwoPoorInsulatedParts !== null;
    }

    const firstAnswer = lead.otherSubsidies?.energyLabelDOrLower;
    if (firstAnswer === null) return false;
    if (firstAnswer === true) return true;
    return lead.otherSubsidies?.hasTwoPoorInsulatedParts !== null;
  })();

  const showVeendamEnergyLabelQuestion = usesVeendamSingleEnergyLabelQuestion && !hasKnownEnergyLabel;
  const showEnergyLabelSplitQuestion =
    usesSplitEnergyQuestions &&
    !usesVeendamSingleEnergyLabelQuestion &&
    !hasKnownEnergyLabel;

  const showTwoPoorPartsQuestion = (() => {
    if (hasKnownEnergyLabel) {
      return usesSplitEnergyQuestions && knownAtMostD === false;
    }

    if (usesVeendamSingleEnergyLabelQuestion) {
      return veendamEnergyLabelCategory !== null && veendamEnergyLabelCategory !== 'dToG';
    }

    const firstAnswer = lead.otherSubsidies?.energyLabelDOrLower;
    return (
      usesSplitEnergyQuestions &&
      !usesVeendamSingleEnergyLabelQuestion &&
      (firstAnswer === false || firstAnswer === 'unknown')
    );
  })();

  const showVhfIsolationQuestion =
    ['Midden-Groningen', 'Oldambt'].includes(municipality) && isSplitSectionComplete;

  return (
    showVeendamEnergyLabelQuestion ||
    showEnergyLabelSplitQuestion ||
    showTwoPoorPartsQuestion ||
    showVhfIsolationQuestion
  );
}

// guard function to be implemented in router as per-route guard (array syntax)
export function FlowGuard(to) {
  let guardResult = FlowService.guard(to);
  if (guardResult) {
    return guardResult;
  }
}

export const FlowService = {
  // The different sections in the flow, in order
  // This array is used to determine which sections are allowed based on 'section' query param
  sectionOrder: ['intake', 'advies'],

  // Secret keys mapping to sections (users need this key in URL to access sections)
  sectionKeys: {
    'intake': null, // no key needed for intake
    'advies': 'adv_8x9mK2pL-1118fc04'
  },

  // list of views in wizard in order
  sequence: [
    {
      name: 'formBellerLoper',
      section: 'intake',
      label: 'Beller/Loper',
      icon: 'bi-person',
      navHide: false,
      disabled() {
        let lead = useLeadStore();
        if (lead.meta.LeadSource == null) {
          return true;
        }
        return !lead.meta.LeadSource.toLowerCase().includes('d2d.bureauverduurzamen.nl')
      },
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.bellerLoper.$invalid;
      }
    },
    {
      name: 'formAddress',
      section: 'intake',
      icon: 'bi-geo-alt',
      label: 'Adres',
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.address.$invalid;
      },
    },
    {
      name: 'formContact', // view name in Vue router
      section: 'intake',
      icon: 'bi-telephone',
      label: 'Contactgegevens',
      navHide: false,
      // function returning boolean indicating if section is complete
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.contact.$invalid;
      },
    },
    {
      name: 'saved',
      section: 'intake',
      label: 'Aanmelding',
      icon: 'bi-floppy',
      navHide: false,
      complete() {
        return true;
      }
    },
    // {
    //   name: 'formCurrentInsulation',
    //   label: 'Huidige isolatie',
    //   icon: 'bi-house',
    //   navHide: false,
    //   complete() {
    //     let v$ = useGlobalVuelidate();
    //     return !v$.value.currentInsulation.$invalid;
    //   },
    // },
    {
      name: 'formPartnerCoupon',
      label: 'Relatiecode',
      navHide: false,
      complete() {
        return true;
      },
      disabled() {
        // let uiStore = useInterfaceStore();
        // return uiStore.skipFormPartnerCoupon;
        return true;
      },
    },
    {
      name: 'formContactPreferences',
      label: 'Voorkeuren',
      icon: 'bi-calendar-check',
      navHide: false,
      disabled() {
        let lead = useLeadStore();
        
        const assignedPartner = (lead.address.assigned_partner || '').toString().trim().toLowerCase();

        if (assignedPartner != 'bureau voor verduurzamen' && !lead.isVhfPekela && !lead.address.eligible) {
          return true;
        }
      },
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.contactPreferences.$invalid;
      },
    },
    {
      name: 'formOwnership',
      section: 'intake',
      label: 'Eigendom',
      icon: 'bi-key',
      navHide: false,
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.ownership.$invalid;
      },
    },
    {
      name: 'formProperty',
      section: 'intake',
      label: 'Woning',
      icon: 'bi-house-door',
      navHide: false,
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.property.$invalid;
      },
    },
    {
      name: 'formFinancingIncome',
      section: 'intake',
      label: 'Extra subsidie',
      icon: 'bi-patch-plus',
      navHide: false,
      disabled() {
        let lead = useLeadStore();
        
        if (lead.address.m29_ontzorging_door_BvV_toegestaan === false && lead.BvvGemeenteAllowed === false) {
          return true;
        }

        return lead.property.InVersterkingsprogramma || lead.address.m29_100pct;
      },
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.financing.income.$invalid;
      },
    },
    {
      name: 'formTwk',
      section: 'intake',
      label: 'Terugwerkende kracht',
      icon: 'bi-clock-history',
      navHide: false,
      disabled() {
        let lead = useLeadStore();
        
        if (lead.address.m29_ontzorging_door_BvV_toegestaan === false && lead.BvvGemeenteAllowed === false) {
          return true;
        }
      },
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.twk.$invalid;
      },
    },
    // {
    //   name: 'formHelpFinancing',
    //   section: 'intake',
    //   label: 'Financiering maatregelen',
    //   icon: 'bi-piggy-bank',
    //   navHide: false,
    //   disabled() {
    //     let lead = useLeadStore();
        
    //     if (lead.address.m29_ontzorging_door_BvV_toegestaan === false) {
    //       return true;
    //     }
    //   },
    //   complete() {
    //     let v$ = useGlobalVuelidate();
    //     return !v$.value.financing.wantHelpFinancing.$invalid;
    //   },
    // },
    {
      name: 'isolatieplanCheck',
      section: 'intake',
      label: 'Isolatieplan',
      icon: 'bi-clipboard-check',
      navHide: false,
      disabled() {
        let lead = useLeadStore();

        if (lead.isIsolatieplanPortalLead) {
          return true;
        }

        return false;
      },
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.isolatieplanCheck.$invalid;
      }
    },
    {
      name: 'isolatieaanpak',
      section: 'intake',
      label: 'Isolatieaanpak',
      icon: 'bi-check-circle',
      navHide: false,
      disabled() {
        let lead = useLeadStore();

        return lead.isIsolatieplanPortalLead;
      },
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.intakeCompletion.$invalid;
      }
    },
    {
      name: 'formIsolatieaanpakSupport',
      section: 'intake',
      label: 'Ondersteuning',
      icon: 'bi-question-circle',
      navHide: false,
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.advice.$invalid;
      }
    },
    {
      name: 'otherSubsidies',
      section: 'intake',
      label: 'Overige subsidies',
      icon: 'bi-file-earmark-text',
      navHide: false,
      disabled() {
        let lead = useLeadStore();

        return !FlowService.hasSubsidyFollowUpSteps() ||
          !MunicipalitySubsidyService.isPageVisible('otherSubsidies', lead, 'financing');
        
      },
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value?.otherSubsidies?.$invalid || true;
      }
    },
    {
      name: 'snnSubsidies',
      section: 'intake',
      label: 'SNN Subsidies',
      icon: 'bi-file-earmark-text',
      navHide: false,
      disabled() {
        let lead = useLeadStore(); 
        
        const assignedPartner = (lead.address.assigned_partner || '').toString().trim().toLowerCase();

        if (assignedPartner != 'bureau voor verduurzamen') {
          return true;
        }
      },
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.financing.snnSubsidyVenV.$invalid;
      }
    },
    {
      name: 'formWozWaarde',
      section: 'intake',
      label: 'WOZ',
      icon: 'bi-file-earmark-text',
      navHide: false,
      disabled(ignoreInterest = false) {
        let lead = useLeadStore();

        if (!ignoreInterest && !lead.otherSubsidies.interestedInOtherSubsidies) {
            return true;
        }

        if (!MunicipalitySubsidyService.isPageVisible('formWozWaarde', lead, 'financing')) {
          return true;
        }
        return lead.address.woz_waarde_ozb !== null;
      },
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.otherSubsidies.woz.$invalid;
      }
    },
    {
      name: 'formSubsidyEnergyLabel',
      section: 'intake',
      label: 'Energielabel of bouwdelen',
      icon: 'bi-house',
      navHide: false,
      disabled(ignoreInterest = false) {
        let lead = useLeadStore();

        if (!ignoreInterest && !lead.otherSubsidies.interestedInOtherSubsidies) {
            return true;
        }

        if (!MunicipalitySubsidyService.isPageVisible('formSubsidyEnergyLabel', lead, 'wozResident')) {
          return true;
        }

        return !hasAnyEnergyLabelQuestion(lead);
      },
      complete() {
        let v$ = useGlobalVuelidate();
        let lead = useLeadStore();
        const municipality = lead.address?.pc_GemNaam;
        const splitMunicipalities = ['Pekela', 'Midden-Groningen', 'Veendam', 'Stadskanaal', 'Oldambt', 'Westerwolde'];

        const splitComplete =
          !splitMunicipalities.includes(municipality) ||
          !v$.value.otherSubsidies.energyLabelDOrLower.$invalid;
        const splitFollowUpComplete =
          !splitMunicipalities.includes(municipality) ||
          !v$.value.otherSubsidies.hasTwoPoorInsulatedParts.$invalid;
        const combinedComplete =
          !splitMunicipalities.includes(municipality) ||
          !v$.value.otherSubsidies.energyLabelDOrLowerOrTwoPoorInsulatedParts.$invalid;
        const veendamVhfComplete =
          municipality !== 'Veendam' ||
          !v$.value.otherSubsidies.vhfEnergyLabelBOrLower.$invalid;
        const vhfIsolationComplete =
          !['Midden-Groningen', 'Oldambt'].includes(municipality) ||
          !v$.value.otherSubsidies.vhfNeedsIsolationStandardMeasures.$invalid;

        return splitComplete && splitFollowUpComplete && combinedComplete && veendamVhfComplete && vhfIsolationComplete;
      }
    },
    {
      name: 'formFinancingVhf',
      section: 'intake',
      label: 'Inkomensvoorwaarden subsidie',
      icon: 'bi-percent',
      navHide: false,
      disabled(ignoreInterest = false) {
        let lead = useLeadStore();

        if (!ignoreInterest && !lead.otherSubsidies.interestedInOtherSubsidies) {
            return true;
        }

        if (lead.address?.pc_GemNaam !== 'Veendam') {
          return true;
        }

        if (lead.financing?.income?.SM_140pct === true) {
          return true;
        }

        return !MunicipalitySubsidyService.isPageVisible('formFinancingVhf', lead, 'property', 'VHF');
      },
      complete() {
        let lead = useLeadStore();
        return lead.financing?.income?.CivilStatus != null &&
          lead.financing?.income?.SM_160pct !== null &&
          lead.financing?.income?.SM_180pct !== null;
      }
    },
    {
      name: 'formSubsidyVhf',
      section: 'intake',
      label: 'Subsidievoorwaarden',
      icon: 'bi-person',
      navHide: false,
      disabled(ignoreInterest = false) {
        let lead = useLeadStore();

        if (!ignoreInterest && !lead.otherSubsidies.interestedInOtherSubsidies) {
            return true;
        }

        return !MunicipalitySubsidyService.isPageVisible('formSubsidyVhf', lead, 'financingVhf', 'VHF');
      },
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.otherSubsidies.vhf.$invalid;
      }
    },
    {
      name: 'formSubsidy',
      section: 'intake',
      label: 'Uitvoering maatregelen',
      icon: 'bi-person',
      navHide: false,
      disabled(ignoreInterest = false) {
        let lead = useLeadStore();

        if (!ignoreInterest && !lead.otherSubsidies.interestedInOtherSubsidies) {
            return true;
        }

        return !MunicipalitySubsidyService.isPageVisible(
          'formSubsidy',
          lead,
          'financingVhf',
        );
      },
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.otherSubsidies.subsidy.$invalid;
      }
    },
    {
      name: 'otherSubsidiesFinal',
      section: 'intake',
      label: 'Subsidieresultaat',
      icon: 'bi-file-earmark-text',
      navHide: false,
      disabled() {
        let lead = useLeadStore();

        if (FlowService.hasSubsidyFollowUpSteps() && !lead.otherSubsidies.interestedInOtherSubsidies) {
            return true;
        }

        return !MunicipalitySubsidyService.isPageVisible('otherSubsidies', lead, 'financing');
        
      },
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value?.otherSubsidies?.$invalid || true;
      }
    },
    {
      name: 'intakeEnd',
      section: 'intake',
      label: 'Samenvatting',
      icon: 'bi-file-earmark-text',
      navHide: false,
      complete() {
        return true;
      }
    },
    {
      name: 'formPropertyAdvies',
      section: 'advies',
      label: 'Woning',
      dividerBefore: true,
      icon: 'bi-house-door',
      navHide: false,
      repeatFrom: 'intake',
      disabled() {
        let v$ = useGlobalVuelidate();
        let lead = useLeadStore();
        
        return !v$.value.property.$invalid && !lead.meta.VisitedViews.includes('formPropertyAdvies');
      },
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.property.$invalid;
      },
    },
    {
      name: 'formFinancingIncomeAdvies',
      section: 'advies',
      label: 'Extra subsidie',
      icon: 'bi-patch-plus',
      navHide: false,
      get dividerBefore() {
        const lead = useLeadStore();
        const v$ = useGlobalVuelidate();

        return !v$.value.property.$invalid && !lead.meta.VisitedViews.includes('formPropertyAdvies');
      },
      disabled() {
        let v$ = useGlobalVuelidate();
        let lead = useLeadStore();        
        

        if (lead.property.InVersterkingsprogramma || lead.address.m29_100pct) {
          return true;
        }
        if (lead.address.m29_ontzorging_door_BvV_toegestaan === false) {
          return true;
        }

        return !v$.value.financing.income.$invalid && !lead.meta.VisitedViews.includes('formFinancingIncomeAdvies');
      },
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.financing.income.$invalid;
      },
    },
    {
      name: 'formTwkAdvies',
      section: 'advies',
      label: 'Terugwerkende kracht',
      icon: 'bi-clock-history',
      navHide: false,
      get dividerBefore() {
        //Add divider if both previous steps are disabled
        const lead = useLeadStore();
        const v$ = useGlobalVuelidate();

        const propertyAdviesDisabled = !v$.value.property.$invalid && !lead.meta.VisitedViews.includes('formPropertyAdvies');
        const financingIncomeAdviesDisabled = lead.property.InVersterkingsprogramma ||
          (!v$.value.financing.income.$invalid && !lead.meta.VisitedViews.includes('formFinancingIncomeAdvies'));

        return propertyAdviesDisabled && financingIncomeAdviesDisabled;
      },
      disabled() {
        let v$ = useGlobalVuelidate();
        let lead = useLeadStore();
        if (lead.address.m29_ontzorging_door_BvV_toegestaan === false) {
          return true;
        }

        return !v$.value.twk.$invalid && !lead.meta.VisitedViews.includes('formTwkAdvies');
      },
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.twk.$invalid;
      },
    },
    {
      name: 'formExecutedMeasures',
      section: 'advies',
      label: 'Uitgevoerde maatregelen',
      dividerBefore: true,
      icon: 'bi-tools',
      navHide: false,
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.executedMeasures.$invalid;
      }
    },
    {
      name: 'formTwkUitgebreid',
      section: 'advies',
      label: 'Twk Advies',
      icon: 'bi-file-earmark-ruled',
      navHide: false,
      disabled() {
        let lead = useLeadStore();
        return lead.executedMeasures.ExecutedMeasures.length === 0;
      },
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.isolatieplan.$invalid;
      }
    },
    // {
    //   name: 'formFinancingNip',
    //   label: 'Gemeentelijke subsidie',
    //   icon: 'bi-bank',
    //   navHide: false,
    //   parent: 'formFinancingIncome',
    //   complete() {
    //     let v$ = useGlobalVuelidate();
    //     return !v$.value.financing.nip.$invalid;
    //   },
    //   disabled: () => {
    //     const lead = useLeadStore();
    //     return !lead.NIP_address;
    //   }
    // },
    // {
    //   name: 'formInterests',
    //   section: 'advies',
    //   label: 'Maatregelen',
    //   icon: 'bi-ui-checks',
    //   navHide: false,
    //   complete() {
    //     let v$ = useGlobalVuelidate();
    //     return !v$.value.interests.$invalid;
    //   }
    // },
    {
      name: 'formWishes',
      section: 'advies',
      label: 'Wensen',
      icon: 'bi-ui-checks',
      navHide: false,
      complete() {
        let v$ = useGlobalVuelidate();
        return !v$.value.wishes.$invalid;
      }
    },
    {
      name: 'formAdviesIsolatieplanInterest',
      section: 'advies',
      label: 'Interesse',
      icon: 'bi-check2',
      navHide: false,
      complete() {
        return true;
      }
    },
    // {
    //   name: 'formIntentions',
    //   section: 'advies',
    //   label: 'Aanpak',
    //   icon: 'bi-bar-chart-steps',
    //   navHide: false,
    //   complete() {
    //     let v$ = useGlobalVuelidate();
    //     return !v$.value.intentions.$invalid;
    //   }
    // },
    // {
    //   name: 'formAdviesInteresses',
    //   section: 'advies',
    //   label: 'Hulp',
    //   icon: 'bi-journal-check',
    //   navHide: false,
    //   complete() {

    //     //TODO
    //     // let v$ = useGlobalVuelidate();
    //     // return !v$.value.intentions.$invalid;
    //   }
    // },
    // {
    //   name: 'formCavityWall',
    //   label: 'Spouwmuur',
    //   navHide: false,
    //   complete() {
    //     let v$ = useGlobalVuelidate();
    //     return !v$.value.cavity_wall.general.$invalid;
    //   },
    //   disabled: measureDisabledFunction('Cavity Wall'),
    // },
    // {
    //   name: 'formCavityWallPaint',
    //   label: 'Gevelverf',
    //   parent: 'formCavityWall',
    //   navHide: true,
    //   complete() {
    //     let v$ = useGlobalVuelidate();
    //     return !v$.value.cavity_wall.paint.$invalid;
    //   },
    //   disabled: measureDisabledFunction('Cavity Wall'),
    // },
    // {
    //   name: 'formCavityWallStucco',
    //   label: 'Gevelstucwerk',
    //   parent: 'formCavityWall',
    //   navHide: true,
    //   complete() {
    //     let v$ = useGlobalVuelidate();
    //     return !v$.value.cavity_wall.stucco.$invalid;
    //   },
    //   disabled: measureDisabledFunction('Cavity Wall'),
    // },
    // {
    //   name: 'formRoof',
    //   label: 'Dakisolatie',
    //   navHide: false,
    //   complete() {
    //     let v$ = useGlobalVuelidate();
    //     return !v$.value.roof.general.$invalid;
    //   },
    //   disabled: measureDisabledFunction('Roof'),
    // },
    // {
    //   name: 'formRoofAttic',
    //   label: 'Zolder',
    //   navHide: true,
    //   parent: 'formRoof',
    //   complete() {
    //     let v$ = useGlobalVuelidate();
    //     return !v$.value.roof.attic.$invalid;
    //   },
    //   disabled: measureDisabledFunction('Roof'),
    // },
    // {
    //   name: 'formRoofFinishing',
    //   label: 'Binnenzijde dak',
    //   navHide: true,
    //   parent: 'formRoof',
    //   complete() {
    //     let v$ = useGlobalVuelidate();
    //     return !v$.value.roof.finishing.$invalid;
    //   },
    //   disabled() {
    //     let lead = useLeadStore();
    //     return lead.interestsArray.includes('Roof') && !lead.skipRoofFinishing
    //       ? false
    //       : true;
    //   },
    // },
    // {
    //   name: 'formFloor',
    //   label: 'Vloerisolatie',
    //   navHide: false,
    //   complete() {
    //     let v$ = useGlobalVuelidate();
    //     return !v$.value.floor.general.$invalid;
    //   },
    //   disabled: measureDisabledFunction('Floor'),
    // },
    // {
    //   name: 'formFloorDetails',
    //   label: 'Kruipruimte details',
    //   parent: 'formFloor',
    //   navHide: true,
    //   complete() {
    //     let v$ = useGlobalVuelidate();
    //     return !v$.value.floor.details.$invalid;
    //   },
    //   disabled: measureDisabledFunction('Floor'),
    // },
    // {
    //   name: 'formGlazing',
    //   label: 'Dubbelglas',
    //   navHide: false,
    //   complete() {
    //     let v$ = useGlobalVuelidate();
    //     return !v$.value.glazing.general.$invalid;
    //   },
    //   disabled: measureDisabledFunction('Glazing'),
    // },
    // {
    //   name: 'formGlazingFrame',
    //   label: 'Kozijnen',
    //   navHide: true,
    //   parent: 'formGlazing',
    //   complete() {
    //     let v$ = useGlobalVuelidate();
    //     return !v$.value.glazing.frame.$invalid;
    //   },
    //   disabled: measureDisabledFunction('Glazing'),
    // },
    // {
    //   name: 'formGlazingDimensions',
    //   label: 'Afmetingen',
    //   navHide: true,
    //   parent: 'formGlazing',
    //   complete() {
    //     let v$ = useGlobalVuelidate();
    //     return !v$.value.glazing.dimensions.$invalid;
    //   },
    //   disabled: measureDisabledFunction('Glazing'),
    // },
    // {
    //   name: 'formHeatpump',
    //   label: 'Warmtepomp',
    //   navHide: false,
    //   complete() {
    //     let v$ = useGlobalVuelidate();
    //     return !v$.value.heatpump.$invalid;
    //   },
    //   disabled: measureDisabledFunction('Heatpump'),
    // },
    // {
    //   name: 'formSolar',
    //   label: 'Zonnepanelen',
    //   navHide: false,
    //   complete() {
    //     let v$ = useGlobalVuelidate();
    //     return !v$.value.solar.$invalid;
    //   },
    //   disabled: measureDisabledFunction('Solar'),
    // },
    // {
    //   name: 'qualificationResult',
    //   section: 'advies',
    //   label: 'Uw mogelijkheden',
    //   dividerBefore: true,
    //   icon: 'bi-trophy-fill',
    //   navHide: false,
    //   complete() {
    //     return enabledSectionsComplete.value;
    //   },
    // },

    {
      name: 'adviesEnd',
      section: 'advies',
      label: 'Bedankt',
      dividerBefore: true,
      icon: 'bi-trophy-fill',
      navHide: false,
      complete() {
        return true;
      },
    },
  ],

  hasSubsidyFollowUpSteps() {
    const firstStep = this.getViewKeyFromName('otherSubsidies') + 1;
    const resultPage = this.getViewKeyFromName('otherSubsidiesFinal');
    return this.sequence.slice(firstStep, resultPage)
      .some((view) => view.disabled(true) === false);
  },

  /**
   * get key of view in sequence array by route name
   * when name param is an integer, it will be returned as-is
   * @param {string|number} name name of the route / view, integers are returned as-is
   * @returns {number}
   */
  getViewKeyFromName(name) {
    if (!name) {
      return null;
    }
    if (isInteger(name)) {
      return name;
    }
    for (let i = 0; i < this.sequence.length; i++) {
      const view = this.sequence[i];
      if (view.name == name) {
        return i;
      }
    }
    return null;
  },

  /**
   * if disabled property is set on view object, check if disabled
   * when disabled prop is a function, the function will be run
   * @param {number} key key of view in sequence
   * @returns {boolean}
   */
  isDisabled(key) {
    if (key in this.sequence) {
      let view = this.sequence[key];
      if ('disabled' in view) {
        if (typeof view.disabled === 'boolean') {
          return view.disabled;
        } else if (isFunction(view.disabled)) {
          return view.disabled();
        }
      }
      return false;
    }
  },

  /**
   * if complete property is set on view object, check if complete
   * when complete prop is a function, the function will be run
   * @param {number} key key of view in sequence
   * @param {boolean} defaultValue value returned if view has no complete property
   * @returns {boolean}
   */
  isComplete(key, defaultValue = false) {
    if (key in this.sequence) {
      let view = this.sequence[key];
      if ('complete' in view) {
        if (typeof view.complete === 'boolean') {
          return view.complete;
        } else if (isFunction(view.complete)) {
          return view.complete();
        }
      }
      return defaultValue;
    }
  },

  /**
   * get first view in sequence where complete() == false
   * @param {boolean} fallbackToLast return last view if all views are complete
   * @returns {string}
   */
  getFirstIncomplete(fallbackToLast = false) {
    for (let i = 0; i < this.sequence.length; i++) {
      const view = this.sequence[i];
      if (!view.complete() && !this.isDisabled(i)) {
        return view.name;
      }
    }
    return fallbackToLast ? this.sequence[this.getLastEnabledKey()].name : null;
  },

  /**
   * get last view in sequence where complete() == true
   * @param {boolean} fallbackToFirst return first view if no views are complete
   * @returns {string}
   */
  getLastComplete(fallbackToFirst = false) {
    for (let i = this.sequence.length - 1; i > 0; i--) {
      const view = this.sequence[i];
      if (this.isComplete(i) && !this.isDisabled(i)) {
        return view.name;
      }
    }
    return fallbackToFirst
      ? this.sequence[this.getFirstEnabledKey()].name
      : null;
  },

  // function to get last enabled view, by iterating backwards through sequence
  getLastEnabledKey() {
    for (let i = this.sequence.length - 1; i >= 0; i--) {
      if (this.isDisabled(i) == false) {
        return i;
      }
    }
    return null;
  },

  // function to get first enabled view
  getFirstEnabledKey() {
    for (let i = 0; i < this.sequence.length; i++) {
      if (this.isDisabled(i) == false) {
        return i;
      }
    }
  },

  /**
   * checks all items BEFORE the given key in the sequence array and returns the first item where complete() == false
   * or returns null if all prerequisite items are complete
   * @param {Number} key key (in sequence array) of the current item for which to check the prerequisites
   * @returns {Number}
   */
  getPreReqTodo(key, allowedSection = null) {
    if (key && key in this.sequence) {
      const targetView = this.sequence[key];
      const targetSectionIndex = this.sectionOrder.indexOf(targetView.section || 'intake');
      const allowedSectionIndex = allowedSection ? this.sectionOrder.indexOf(allowedSection) : 0;

      for (let i = 0; i < key; i++) {
        const view = this.sequence[i];
        const viewSectionIndex = this.sectionOrder.indexOf(view.section || 'intake');
        
        // If user has higher section access, skip validation for lower sections
        if (viewSectionIndex < targetSectionIndex) {
          continue;
        }

        // 2) Valideer nooit voorbij de maximaal toegestane sectie
        if (viewSectionIndex > allowedSectionIndex) {
          continue;
        }
        
        if (!view.complete() && !this.isDisabled(i)) {
          return i;
        }
      }
    }
    return null;
  },

  /**
   * get route name of next enabled view in sequence
   * @param {String} viewName route name of current view
   * @returns {String} route name of next view
   */
  getNext(viewName) {
    let currentViewKey = this.getViewKeyFromName(viewName);
    // if not last view
    if (!this.isLast(viewName)) {
      // iterate through subsequent views
      for (let i = currentViewKey + 1; i < this.sequence.length; i++) {
        // if not disabled, return name
        if (this.isDisabled(i) == false) {
          return this.sequence[i].name;
        }
      }
    }
    return null;
  },
  /**
   * variant of getNext that immediately executed a $router.push with params
   * @param {object} vm vue component (this)
   */
  pushNext(vm, query = {}) {
    vm.$router.push({
      name: this.getNext(vm.$route.name),
      params: vm.$route.params,
      query: {...StripQuery(vm.$route.query), ...query}
    });
  },

  /**
   * get route name of previous enabled view in sequence
   * @param {String} viewName route name of current view
   * @returns {String} route name of previous view
   */
  getPrev(viewName) {
    let currentViewKey = this.getViewKeyFromName(viewName);
    // if not first view
    if (!this.isFirst(viewName)) {
      // iterate backwards through previous views
      for (let i = currentViewKey - 1; i >= 0; i--) {
        // if not disabled, return name
        if (this.isDisabled(i) == false) {
          return this.sequence[i].name;
        }
      }
    }
    return null;
  },
  /**
   * variant of getPrev that immediately executed a $router.push with params
   * @param {object} vm vue component (this)
   */
  pushPrev(vm, query = {}) {
    vm.$router.push({
      name: this.getPrev(vm.$route.name),
      params: vm.$route.params,
      query: {...StripQuery(vm.$route.query), ...query}
    });
  },

  /**
   * check if view is last view in sequence by route name
   * @param {string|number} viewNameKey name or key of view to check
   * @returns {boolean}
   */
  isLast(viewNameKey) {
    // get key of current view
    let viewKey = this.getViewKeyFromName(viewNameKey);
    // check if current view is last enabled view
    if (viewKey !== null) {
      return viewKey == this.getLastEnabledKey();
    }
    return false;
  },

  /**
   * check if view is first view in sequence by route name
   * @param {string|number} viewNameKey name or key of view to check
   * @returns {boolean}
   */
  isFirst(viewNameKey) {
    // get key of current view
    let viewKey = this.getViewKeyFromName(viewNameKey);
    // check if current view is first enabled view
    if (viewKey !== null) {
      return viewKey == this.getFirstEnabledKey();
    }
    return false;
  },

  /**
   * Determine which section is allowed based on the section query parameter
   * @param {string} sectionParam - value from ?section= 
   * @returns {string} - allowed section name
   */
  getAllowedSection(sectionParam) {
    // Check if the provided sectionParam matches any of our section keys
    for (const [section, key] of Object.entries(this.sectionKeys)) {
      if (sectionParam === key) {
        return section;
      }
    }
    // Default to intake if no match
    return 'intake';
  },

  /**
   * Check if a view has a "forced complete" (returns true without validation)
   * @param {Object} view - view object from sequence
   * @returns {boolean}
   */
  isForcedComplete(view) {
    if (!view.complete || typeof view.complete !== 'function') {
      return false;
    }
    
    const completeSource = view.complete.toString();
    // Check if the function just returns true (forced complete)
    // Exclude functions that use v$ (validation)
    return completeSource.includes('return true') && !completeSource.includes('v$');
  },

  /**
   * Find the last view with actual validation (non-forced complete)
   * @param {number} currentViewKey - current position in sequence
   * @param {number} maxSectionIndex - maximum allowed section index
   * @returns {number|null} - key of last validated view, or null if none found
   */
  findLastValidatedView(currentViewKey, maxSectionIndex) {
    // Start from current view and search backwards
    const searchStart = currentViewKey !== null ? currentViewKey : this.sequence.length - 1;
    
    for (let i = searchStart; i >= 0; i--) {
      const view = this.sequence[i];
      const viewSectionIndex = this.sectionOrder.indexOf(view.section || 'intake');
      
      // Skip if beyond allowed section
      if (viewSectionIndex > maxSectionIndex) continue;
      
      // Skip disabled views
      if (this.isDisabled(i)) continue;
      
      // Check if this view has real validation (not forced complete)
      if (view.complete && typeof view.complete === 'function' && !this.isForcedComplete(view)) {
        return i;
      }
    }
    return null;
  },

  /**
   * Find the next view with forced complete() starting from a given position
   * @param {number} startKey - starting position in sequence
   * @param {number} maxSectionIndex - maximum allowed section index
   * @returns {number|null} - key of next forced complete view, or null if none found
   */
  findNextForcedComplete(startKey, maxSectionIndex) {
    for (let i = startKey; i < this.sequence.length; i++) {
      const view = this.sequence[i];
      const viewSectionIndex = this.sectionOrder.indexOf(view.section || 'intake');
      
      // Stop if we're beyond allowed section
      if (viewSectionIndex > maxSectionIndex) break;
      
      // Skip disabled views
      if (this.isDisabled(i)) continue;
      
      // Check if this is a forced complete
      if (this.isForcedComplete(view)) {
        return i;
      }
    }
    return null;
  },

  getNavItems(currentViewName, sectionParam = 'intake') {
    let currentViewKey = this.getViewKeyFromName(currentViewName);

    // Determine max allowed section based on sectionKey validation
    const allowedSection = this.getAllowedSection(sectionParam);
    const maxSectionIndex = this.sectionOrder.indexOf(allowedSection);
    
    let lastViewKey;
    if (allowedSection === 'advies') {
      // Show all fields up to and including current view
      let lastAllowed = currentViewKey || 0;
      for (let i = this.sequence.length - 1; i >= 0; i--) {
        const secIdx = this.sectionOrder.indexOf(this.sequence[i].section || 'intake');
        if (secIdx <= maxSectionIndex) {
          lastAllowed = i;
          break;
        }
      }
      lastViewKey = lastAllowed;
    } else {
      const lastValidatedKey = this.findLastValidatedView(currentViewKey, maxSectionIndex);
      const searchStartKey = lastValidatedKey !== null ? lastValidatedKey : 0;
      const nextForcedCompleteKey = this.findNextForcedComplete(
        searchStartKey, 
        maxSectionIndex
      );

      // use next force complete as end
      lastViewKey = nextForcedCompleteKey !== null 
        ? nextForcedCompleteKey 
        : (currentViewKey || 0);
    }

    // get all enabled items before key
    let items = [];
    for (let i = 0; i <= lastViewKey; i++) {
      const view = this.sequence[i];
      const viewSectionIndex = this.sectionOrder.indexOf(view.section || 'intake');
      const isActiveChild =
        currentViewKey &&
        ((this.sequence[currentViewKey].parent &&
          this.sequence[currentViewKey].parent == view.parent) ||
          (this.sequence[currentViewKey].parent &&
            this.sequence[currentViewKey].parent == view.name) ||
          (view.parent && this.sequence[currentViewKey].name == view.parent))
          ? true
          : false;
      if (
        viewSectionIndex <= maxSectionIndex &&
        (
        i == currentViewKey ||
        isActiveChild ||
        (!this.isDisabled(i) && !view.navHide)
      )) {
        items.push({
          ...view,
          activeChild: i !== currentViewKey && isActiveChild ? true : false,
          beforeCurrent: i < currentViewKey,
          afterCurrent: i > currentViewKey,
          isCurrent: i == currentViewKey,
        });
      }
    }
    return items;
  },

  /**
   * reusable guard method (per-route guard)
   * checks prerequisite items in sequence (getPreReqTodo())
   * if any of those is not complete, the method will return a redirect object to redirect to the first incomplete view
   * Additionally, checks if the requested view is in an allowed section (based on the 'section' query parameter).
   * If the requested view is in a section beyond what is allowed, redirects to the last allowed view in that section.
   * @param {Object} to view object we are navigating to
   * @returns {Object}
   */
  guard(to) {
    // check if prerequisite views are complete
    let viewKey = this.getViewKeyFromName(to.name);
    if (viewKey === null) {
      return;
    }

    const view = this.sequence[viewKey];

    // Determine allowed section based on sectionKey validation

    const sectionParam = to.query.section || 'intake';
    const allowedSection = this.getAllowedSection(sectionParam);

    const maxSectionIndex = this.sectionOrder.indexOf(allowedSection);
    const viewSectionIndex = this.sectionOrder.indexOf(view.section || 'intake');

    if (allowedSection === 'advies' && viewSectionIndex === 0) {
      return;
    }

    // Check if the requested view is in a section beyond what is allowed by the query param.
    // If so, redirect to the last allowed view in the permitted section.
    if (viewSectionIndex > maxSectionIndex) {
      const lastAllowedKey = [...this.sequence]
        .map((v, i) => ({...v, i}))
        .filter(v => this.sectionOrder.indexOf(v.section || 'intake') === maxSectionIndex)
        .pop()?.i;
      if (lastAllowedKey !== undefined) {
        return {
          name: this.sequence[lastAllowedKey].name,
          params: to.params,
          query: StripQuery(to.query, ['from'])
        };
      }
    }

    // if (allowedSection === 'advies' && viewSectionIndex === 0) {
    //   // User has advies access and is navigating within intake section - allow it
    //   return;
    // }

    // Check for incomplete prerequisite views and redirect if necessary.
    let prereqTodo = this.getPreReqTodo(viewKey, allowedSection);
    if (prereqTodo !== null) {
      console.log(
        'redirecting to incomplete prerequisite view: ',
        this.sequence[prereqTodo].name
      ); // #REMOVE BEFORE FLIGHT
      return {
        name: this.sequence[prereqTodo].name,
        params: to.params,
        query: StripQuery(to.query, ['from'])
      };
    }
  },
};

/**
 * return disabled() function
 * @param {string} value api-name of measure to check
 * @returns {boolean}
 */
export function measureDisabledFunction(value) {
  return function () {
    let lead = useLeadStore();
    return lead.interestsArray.includes(value) ? false : true;
  };
}
